/**
 * AcademyPage — /academia
 *
 * Documentación viva de la aplicación. Rail de capítulos a la izquierda,
 * lector a la derecha y buscador arriba. La ruta es /academia/<capitulo>/<tema>.
 *
 * Contenido: `registry.js`. Cómo se mantiene: `docs/ACADEMIA_CMDB.md`.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MagnifyingGlassIcon, BookOpenIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ACADEMY_CHAPTERS, allTopics, getChapterOfTopic, getTopic, searchTopics } from './registry';
import BlockRenderer from './blocks';
import './academy.css';

function formatDate(iso) {
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export default function AcademyPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const segments = (params['*'] || '').split('/').filter(Boolean);
  const requestedTopicId = segments[1] || null;
  const activeTopic = requestedTopicId ? getTopic(requestedTopicId) : null;
  const activeChapter = activeTopic ? getChapterOfTopic(activeTopic.id) : null;

  const searching = query.trim().length > 0;
  const results = useMemo(() => (searching ? searchTopics(query) : []), [query, searching]);
  const totalTopics = useMemo(() => allTopics().length, []);

  const openTopic = (chapterId, topicId) => {
    setQuery('');
    navigate(`/academia/${chapterId}/${topicId}`);
  };

  return (
    <div className="ac-page">
      <header className="ac-hero">
        <div className="ac-hero-text">
          <span className="ac-eyebrow">Documentación viva</span>
          <h1 className="ac-title"><BookOpenIcon className="ac-title-icon" /> Academia CMDB</h1>
          <p className="ac-subtitle">
            {totalTopics} temas con capturas de la propia aplicación y sandboxes para probar sin riesgo.
            Cada ficha lleva su «?» que abre el tema exacto.
          </p>
        </div>
        <div className="ac-search">
          <MagnifyingGlassIcon className="ac-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="ac-search-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Busca: rack, exportar, columnas, favoritos…"
            aria-label="Buscar en la Academia"
          />
        </div>
      </header>

      <div className="ac-body">
        <nav className="ac-rail" aria-label="Temas de la Academia">
          {searching ? (
            <>
              <div className="ac-rail-title">Resultados ({results.length})</div>
              {results.length === 0 && <p className="ac-rail-empty">Nada coincide con «{query}».</p>}
              <ul className="ac-rail-list">
                {results.map((topic) => (
                  <li key={topic.id}>
                    <button
                      type="button"
                      className="ac-rail-item"
                      onClick={() => openTopic(topic.chapterId, topic.id)}
                    >
                      <span className="ac-rail-item-title">{topic.title}</span>
                      <span className="ac-rail-item-chapter">{topic.chapterTitle}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            ACADEMY_CHAPTERS.map((chapter) => (
              <section key={chapter.id} className="ac-rail-chapter">
                <Link to="/academia" className="ac-rail-chapter-title">
                  <span aria-hidden="true">{chapter.icon}</span> {chapter.title}
                </Link>
                <ul className="ac-rail-list">
                  {chapter.topics.map((topic) => {
                    const isActive = activeTopic?.id === topic.id;
                    return (
                      <li key={topic.id}>
                        <button
                          type="button"
                          className={`ac-rail-item${isActive ? ' is-active' : ''}`}
                          onClick={() => openTopic(chapter.id, topic.id)}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {topic.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </nav>

        <main className="ac-content">
          {searching ? (
            <section className="ac-intro">
              <h2 className="ac-content-title">{results.length} tema{results.length === 1 ? '' : 's'} para «{query}»</h2>
              <div className="ac-cards">
                {results.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    className="ac-card"
                    onClick={() => openTopic(topic.chapterId, topic.id)}
                  >
                    <span className="ac-card-chapter">{topic.chapterTitle}</span>
                    <span className="ac-card-title">{topic.title}</span>
                    <span className="ac-card-summary">{topic.summary}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : activeTopic && activeChapter ? (
            <article className="ac-topic">
              <nav className="ac-breadcrumb" aria-label="Miga de pan">
                <button type="button" className="ac-crumb" onClick={() => navigate('/academia')}>Academia</button>
                <span className="ac-crumb-sep">/</span>
                <span className="ac-crumb is-current">{activeChapter.title}</span>
              </nav>

              <header className="ac-topic-head">
                <h2 className="ac-topic-title">{activeTopic.title}</h2>
                {activeTopic.summary && <p className="ac-topic-summary">{activeTopic.summary}</p>}
                <div className="ac-topic-meta">
                  <span className="ac-topic-revised">Revisado el {formatDate(activeTopic.revisedOn)}</span>
                  {activeTopic.route && (
                    <Link className="ac-topic-open" to={activeTopic.route}>
                      Abrir la pantalla <ArrowTopRightOnSquareIcon className="ac-topic-open-icon" />
                    </Link>
                  )}
                </div>
              </header>

              <BlockRenderer blocks={activeTopic.blocks} />
            </article>
          ) : (
            <section className="ac-intro">
              <h2 className="ac-content-title">¿Qué quieres aprender?</h2>
              <p className="ac-content-lead">
                Empieza por el menú si acabas de llegar. Si vienes con una tarea concreta, busca o entra
                en el capítulo que toque: todo lo que se explica aquí se puede probar en la propia página.
              </p>
              <div className="ac-cards">
                {ACADEMY_CHAPTERS.map((chapter) => (
                  <button
                    key={chapter.id}
                    type="button"
                    className="ac-card"
                    onClick={() => openTopic(chapter.id, chapter.topics[0].id)}
                  >
                    <span className="ac-card-icon" aria-hidden="true">{chapter.icon}</span>
                    <span className="ac-card-title">{chapter.title}</span>
                    <span className="ac-card-summary">{chapter.summary}</span>
                    <span className="ac-card-count">{chapter.topics.length} tema{chapter.topics.length === 1 ? '' : 's'}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
